/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChartLegendCell,
  chartLegendCellBar,
  chartLegendCellLine,
  chartLegendRow,
  ChartSpecsWithLegend,
} from '@gh/builder-shared-charts';
import {
  DashboardChartType,
  MonthlySalesSlabsDto,
  MonthlySalesSlabsTooltip,
  MonthlySalesSlabsValue,
  MonthlySalesSlabsYValue,
} from '@gh/builder-shared-data';
import { DateTimeTypes } from '@gh/common-shared';
import { colorLuminance, offsetDate } from '@gh/core-util';
import {
  chunk,
  fill,
  get,
  head,
  isString,
  join,
  last,
  map,
  maxBy,
  uniq,
  values,
  reduce,
} from 'lodash';

export type MonthlySalesSlabs = MonthlySalesSlabsDto;
export const monthlySalesSlabsFromDto = (dto: MonthlySalesSlabsDto, labels: any): ChartSpecsWithLegend => {
  let maxCols: number = 1;
  const colors: string[][] = [
    [],
    [],
    [],
    [],
  ];
  const dataTable: any[][] = [];
  const axisLayout: string[] = [];
  const options: any = {
    isStacked: true,
    seriesType: 'bars',
    pointSize: 0,
    series: {},
  };
  const legendCells: ChartLegendCell[] = [];

  const buildTooltip = (type: DashboardChartType, tooltipItems: MonthlySalesSlabsTooltip[]): string => {
    const typeValue = labels[type] || '';
    let tooltipHtml: string = `<div>${typeValue}</div>`;
    tooltipItems.forEach((item: MonthlySalesSlabsTooltip) => {
      const row = join(values(item), ': ');
      tooltipHtml += `<div>${row}</div>`;
    });

    return tooltipHtml;
  };

  const getLabel = (type: DashboardChartType): string => {
    switch (type) {
      case DashboardChartType.Sales:
        return 'lbl_current_sales';
      case DashboardChartType.SalesPrior:
        return 'lbl_prior_sales';
      case DashboardChartType.Slab:
        return 'lbl_current_slabs';
      case DashboardChartType.SlabPrior:
        return 'lbl_prior_slabs';
      default:
        return '';
    }
  };

  dto.values.forEach((value: MonthlySalesSlabsValue) => {
    const maxYValue = maxBy(value.yAxisValues, (yValue: MonthlySalesSlabsYValue) => yValue.values.length);
    if (maxYValue && maxCols < maxYValue.values.length) {
      maxCols = maxYValue.values.length;
    }
  });

  dataTable.push([
    { type: 'string', label: '' },
  ]);

  const baseColors = ['#01579B', '#FF6F00', '#4A148C', '#B71C1C'];
  const colorStep = 1 / maxCols;

  baseColors.forEach((baseColor: string, baseColorIndex: number) => {
    for (let i = 0; i < 1; i += colorStep) {
      colors[baseColorIndex].push(colorLuminance(baseColor, i * 2));
    }
  });

  const types = map(get(head(dto.values), 'yAxisValues'), 'type');
  types.forEach((type, typeIndex: number) => {
    if (type !== DashboardChartType.SalesBudgets) {
      for (let i = 0; i < maxCols; i++) {
        head(dataTable)!.push(...[
          { type: 'number', label: getLabel(type) },
          { type: 'string', role: 'tooltip', p: { html: true } },
        ]);

        const color = colors[typeIndex][i];
        options.colors = options.colors || [];
        options.colors.push(color);
      }

      legendCells.push(chartLegendCellBar(getLabel(type), baseColors[typeIndex]));
    }
  });

  head(dataTable)!.push(...[
    { type: 'number', label: 'lbl_current_budget' },
    { type: 'string', role: 'tooltip', p: { html: true } },
  ]);

  options.series[(types.length - 1) * maxCols] = { type: 'line', color: '#00ca00' };
  legendCells.push(chartLegendCellLine('lbl_current_budget', '#00ca00'));

  if (maxCols > 1) {
    const seriesIndexes: string[] = [];
    head(dataTable)!
      .filter((item) => item.label)
      .forEach((item: any, index: number) => {
        if (-1 === seriesIndexes.indexOf(item.label)) {
          seriesIndexes.push(item.label);
        } else {
          options.series[index] = { visibleInLegend: false };
        }
      });
  }

  const budgetValues: MonthlySalesSlabsYValue[] = [];
  dto.values.forEach((value: MonthlySalesSlabsValue) => {
    const rowValues: any[][] = [];
    value.yAxisValues.forEach((yValue: MonthlySalesSlabsYValue, yValueIndex: number) => {
      if (yValue.type === DashboardChartType.SalesBudgets) {
        budgetValues.push(yValue);
      } else {
        const date = DateTimeTypes.ShortMonthYear
          .format(offsetDate(new Date(value.xAxisValue)))
          .split(' ');

        const prevYear = Number(date[1]) - 1;
        const dateValue = `${date[0]} ${date[1]}/${prevYear}`;
        axisLayout.push(dateValue);

        rowValues.push([
          0 === yValueIndex ? dateValue : null,
          ...fill(Array(head(dataTable)!.length - 1), null),
        ]);

        for (let i = 0; i < maxCols; i++) {
          last(rowValues)![i * 2 + 1 + maxCols * yValueIndex * 2] = yValue.values[i] || null;
          last(rowValues)![i * 2 + 2 + maxCols * yValueIndex * 2] = buildTooltip(yValue.type, yValue.tooltip);
        }
      }
    });

    if (dto.values.length > 1) {
      rowValues.push([null, ...fill(Array(head(dataTable)!.length - 1), null)]);
    }

    dataTable.push(...rowValues);
  });

  let budgetValue: any;
  dataTable.forEach((row: any, rowIndex: number) => {
    const label = head(row);
    if (label && isString(label)) {
      budgetValue = budgetValues.shift();
    }

    if (rowIndex) {
      row[head(dataTable)!.length - 1] = buildTooltip(budgetValue.type, budgetValue.tooltip);
      row[head(dataTable)!.length - 2] = reduce(budgetValue.values, (sum: number, n: number) => sum + n, 0);
    }
  });

  return {
    legendLayout: chunk(legendCells, 3).map((cells) => chartLegendRow(...cells)),
    axisLayout: uniq(axisLayout),
    chartType: 'ComboChart',
    dataTable,
    options,
  };
};
